import { createApi } from "@reduxjs/toolkit/query/react";
import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import {
  API_CHAT_ATTACHMENTS,
  API_CHAT_AUDIENCE_MEMBERS,
  API_CHAT_AUDIENCES,
  API_CHAT_BROADCAST,
  API_CHAT_CAPABILITIES,
  API_CHAT_CONTACTS,
  API_CHAT_CONVERSATION,
  API_CHAT_CONVERSATION_ACTION,
  API_CHAT_CONVERSATIONS,
  API_CHAT_DIRECT,
  API_CHAT_GROUP,
  API_CHAT_MEMBER,
  API_CHAT_MESSAGE,
  API_CHAT_MESSAGE_PLAYED,
  API_CHAT_MESSAGE_REACTION,
  API_CHAT_MESSAGE_PIN,
  API_CHAT_MESSAGE_FORWARD,
  API_CHAT_CONVERSATION_PINS,
  API_CHAT_PINNED_CONVERSATIONS,
  API_CHAT_MESSAGE_VOTE,
  API_CHAT_SCHEDULED,
  API_CHAT_SCHEDULED_SEND,
  API_CHAT_SETTINGS,
  API_CHAT_CHANNEL_NOTICES,
  API_CHAT_SEARCH,
  API_CHAT_SHORTCUT_ITEMS,
  API_CHAT_UNREAD_COUNT,
  API_CHAT_WORK_STATUS,
  API_CHAT_WORK_STATUS_RECIPIENTS,
} from "@/constants/apiEndpoints";
import type {
  ChatNoticeKind,
  ChatAudienceKind,
  ChatListTab,
  ChatWallpaper,
  ChatMuteDuration,
  ChatShortcutEntityType,
  ChatWorkStatus,
  IChatAudienceMembers,
  IChatAudiences,
  IChatContact,
  IChatConversation,
  IChatConversationDetail,
  IChatListResponse,
  IChatMediaItem,
  IChatMessage,
  IChatMessagePage,
  IChatMessageSearchPage,
  IChatGlobalSearchPage,
  IChatOutgoingMessage,
  IChatScheduledMessage,
  IChatScheduleRequest,
  IChatSettings,
  IChatShortcutItem,
  IChatUnreadSummary,
  IChatUploadedFile,
  IChatWorkStatusRecipients,
} from "@/types/(my-space)/chat.interface";

type Envelope<T> = { data: T; message?: string };

const LIST_PAGE_SIZE = 20;
const CONTACTS_PAGE_SIZE = 30;

const nextPage = (result: IChatListResponse<unknown> | undefined) => {
  if (!result?.meta) return undefined;
  const { currentPage, totalPages } = result.meta;
  return currentPage < totalPages ? currentPage + 1 : undefined;
};

/**
 * `/chat/*`. Messages are not cached here — the room keeps them in local state
 * (optimistic sends, socket upserts), so only the lazy fetchers are used.
 * Socket events invalidate `Conversations` / `Conversation` / `Unread`, and
 * `scheduled_updated` my `Scheduled` list for that conversation.
 */
/** [pinned] in [ids]' order; any id it doesn't hold is skipped (a stale list). */
const reorderPinned = (pinned: IChatMessage[], ids: number[]) => {
  const byId = new Map(pinned.map((m) => [m.id, m]));
  return ids.flatMap((id) => byId.get(id) ?? []);
};

export const chatApi = createApi({
  reducerPath: "chatApi",
  baseQuery: dynamicBaseQuery,
  refetchOnFocus: false,
  tagTypes: ["Conversations", "Conversation", "Unread", "Media", "Settings", "Scheduled"],
  endpoints: (builder) => ({
    getChatCapabilities: builder.query<{ canBroadcast: boolean }, void>({
      query: () => ({ url: API_CHAT_CAPABILITIES }),
      transformResponse: (r: Envelope<{ canBroadcast: boolean }>) => r.data,
    }),

    getChatSettings: builder.query<IChatSettings, void>({
      query: () => ({ url: API_CHAT_SETTINGS }),
      transformResponse: (r: Envelope<IChatSettings>) => r.data,
      providesTags: ["Settings"],
    }),

    /** My default background — every chat without its own follows it. */
    updateChatSettings: builder.mutation<IChatSettings, IChatSettings>({
      query: (body) => ({ url: API_CHAT_SETTINGS, method: "PATCH", body }),
      transformResponse: (r: Envelope<IChatSettings>) => r.data,
      invalidatesTags: ["Settings", "Conversation"],
    }),

    /** This chat's own background (for me); null follows my default. */
    /** Keep or stop a kind of channel notice (settings toggle, "Is this useful?"). */
    setChannelNotice: builder.mutation<IChatSettings, { kind: ChatNoticeKind; keep: boolean }>({
      query: (body) => ({ url: API_CHAT_CHANNEL_NOTICES, method: "PATCH", body }),
      transformResponse: (r: Envelope<IChatSettings>) => r.data,
      // Answered at once on screen; the server's copy replaces it.
      async onQueryStarted({ kind, keep }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          chatApi.util.updateQueryData("getChatSettings", undefined, (draft) => {
            draft.channelNotices = { ...(draft.channelNotices ?? {}), [kind]: keep };
          }),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(chatApi.util.upsertQueryData("getChatSettings", undefined, data));
        } catch {
          patch.undo();
        }
      },
    }),

    setChatWallpaper: builder.mutation<
      IChatConversationDetail,
      { id: number; wallpaper: ChatWallpaper | null }
    >({
      query: ({ id, wallpaper }) => ({
        url: API_CHAT_CONVERSATION_ACTION(id, "wallpaper"),
        method: "PATCH",
        body: { wallpaper },
      }),
      transformResponse: (r: Envelope<IChatConversationDetail>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: "Conversation", id }],
    }),

    getChatUnreadSummary: builder.query<IChatUnreadSummary, void>({
      query: () => ({ url: API_CHAT_UNREAD_COUNT }),
      transformResponse: (r: Envelope<IChatUnreadSummary>) => r.data,
      providesTags: ["Unread"],
    }),

    getChatConversations: builder.infiniteQuery<
      IChatListResponse<IChatConversation>,
      { tab: ChatListTab; archived: boolean; search?: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (result) => nextPage(result),
      },
      query: ({ queryArg, pageParam }) => ({
        url: API_CHAT_CONVERSATIONS,
        params: {
          tab: queryArg.tab,
          archived: queryArg.archived,
          search: queryArg.search?.trim() || undefined,
          page: pageParam,
          limit: LIST_PAGE_SIZE,
        },
      }),
      providesTags: ["Conversations"],
    }),

    getChatContacts: builder.infiniteQuery<
      IChatListResponse<IChatContact>,
      { search?: string },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (result) => nextPage(result),
      },
      query: ({ queryArg, pageParam }) => ({
        url: API_CHAT_CONTACTS,
        params: {
          search: queryArg.search?.trim() || undefined,
          page: pageParam,
          limit: CONTACTS_PAGE_SIZE,
        },
      }),
    }),

    getChatConversation: builder.query<IChatConversationDetail, number>({
      query: (id) => ({ url: API_CHAT_CONVERSATION(id) }),
      transformResponse: (r: Envelope<IChatConversationDetail>) => r.data,
      providesTags: (_r, _e, id) => [{ type: "Conversation", id }],
    }),

    /** One of `before` / `after` / `around` (centred on that message), or none: the newest page. */
    getChatMessages: builder.query<
      IChatMessagePage,
      { conversationId: number; before?: number; after?: number; around?: number }
    >({
      query: ({ conversationId, before, after, around }) => ({
        url: API_CHAT_CONVERSATION_ACTION(conversationId, "messages"),
        params: { before, after, around, limit: 30 },
      }),
      keepUnusedDataFor: 0,
    }),

    /** Messages matching `q` (2–100 chars), newest first, paged by `before`. */
    searchChatMessages: builder.infiniteQuery<
      IChatMessageSearchPage,
      { conversationId: number; q: string },
      number | null
    >({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (last) =>
          last.meta.hasMore && last.meta.nextBefore != null ? last.meta.nextBefore : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: API_CHAT_CONVERSATION_ACTION(queryArg.conversationId, "messages/search"),
        params: { q: queryArg.q, before: pageParam ?? undefined, limit: 20 },
      }),
      keepUnusedDataFor: 30,
    }),

    /** Messages matching `q` in all my chats (the list's search), newest first. */
    searchAllChatMessages: builder.infiniteQuery<IChatGlobalSearchPage, { q: string }, number | null>({
      infiniteQueryOptions: {
        initialPageParam: null,
        getNextPageParam: (last) =>
          last.meta.hasMore && last.meta.nextBefore != null ? last.meta.nextBefore : undefined,
      },
      query: ({ queryArg, pageParam }) => ({
        url: API_CHAT_SEARCH,
        params: { q: queryArg.q, before: pageParam ?? undefined, limit: 20 },
      }),
      keepUnusedDataFor: 30,
    }),

    getChatMedia: builder.infiniteQuery<
      IChatListResponse<IChatMediaItem>,
      /** media: photos + videos sent as media; documents: anything sent as a file. */
      { conversationId: number; kind: "media" | "documents" },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (result) => nextPage(result),
      },
      query: ({ queryArg, pageParam }) => ({
        url: API_CHAT_CONVERSATION_ACTION(queryArg.conversationId, "media"),
        params: { kind: queryArg.kind, page: pageParam, limit: 30 },
      }),
      providesTags: ["Media"],
    }),

    getChatWorkStatusRecipients: builder.query<
      IChatWorkStatusRecipients,
      ChatWorkStatus
    >({
      query: (workStatus) => ({
        url: API_CHAT_WORK_STATUS_RECIPIENTS,
        params: { workStatus },
      }),
      transformResponse: (r: Envelope<IChatWorkStatusRecipients>) => r.data,
      keepUnusedDataFor: 0,
    }),

    /** What "Add by" may offer me; the server applies the permissions. */
    getChatAudiences: builder.query<IChatAudiences, void>({
      query: () => ({ url: API_CHAT_AUDIENCES }),
      transformResponse: (r: Envelope<IChatAudiences>) => r.data,
      keepUnusedDataFor: 60,
    }),

    /** The colleagues behind one or more audiences, deduped, me excluded. */
    getChatAudienceMembers: builder.query<
      IChatAudienceMembers,
      { kind: ChatAudienceKind; ids?: number[] }
    >({
      query: ({ kind, ids }) => ({
        url: API_CHAT_AUDIENCE_MEMBERS,
        params: { kind, ...(ids?.length ? { ids: ids.join(",") } : {}) },
      }),
      transformResponse: (r: Envelope<IChatAudienceMembers>) => r.data,
      keepUnusedDataFor: 0,
    }),

    /** My shareable items of one kind, newest first (≤30); `search` server-side. */
    getChatShortcutItems: builder.query<
      IChatShortcutItem[],
      { type: ChatShortcutEntityType; search?: string }
    >({
      query: ({ type, search }) => ({
        url: API_CHAT_SHORTCUT_ITEMS,
        params: { type, ...(search?.trim() ? { search: search.trim() } : {}) },
      }),
      transformResponse: (r: Envelope<IChatShortcutItem[]>) => r.data,
      keepUnusedDataFor: 30,
    }),

    openChatDirect: builder.mutation<IChatConversationDetail, number>({
      query: (userId) => ({
        url: API_CHAT_DIRECT,
        method: "POST",
        body: { userId },
      }),
      transformResponse: (r: Envelope<IChatConversationDetail>) => r.data,
      invalidatesTags: ["Conversations"],
    }),

    createChatGroup: builder.mutation<
      IChatConversationDetail,
      { kind: "group" | "broadcast"; name: string; memberIds: number[] }
    >({
      query: ({ kind, name, memberIds }) => ({
        url: kind === "group" ? API_CHAT_GROUP : API_CHAT_BROADCAST,
        method: "POST",
        body: { name, memberIds },
      }),
      transformResponse: (r: Envelope<IChatConversationDetail>) => r.data,
      invalidatesTags: ["Conversations"],
    }),

    createChatWorkStatusBroadcast: builder.mutation<
      IChatConversationDetail,
      { workStatus: ChatWorkStatus; message: string; name?: string }
    >({
      query: (body) => ({ url: API_CHAT_WORK_STATUS, method: "POST", body }),
      transformResponse: (r: Envelope<IChatConversationDetail>) => r.data,
      invalidatesTags: ["Conversations"],
    }),

    updateChatConversation: builder.mutation<
      IChatConversationDetail,
      { id: number; name: string }
    >({
      query: ({ id, name }) => ({
        url: API_CHAT_CONVERSATION(id),
        method: "PATCH",
        body: { name },
      }),
      transformResponse: (r: Envelope<IChatConversationDetail>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [
        "Conversations",
        { type: "Conversation", id },
      ],
    }),

    setChatImage: builder.mutation<
      IChatConversationDetail,
      { id: number; image: File }
    >({
      query: ({ id, image }) => {
        const body = new FormData();
        body.append("image", image);
        return {
          url: API_CHAT_CONVERSATION_ACTION(id, "image"),
          method: "POST",
          body,
        };
      },
      transformResponse: (r: Envelope<IChatConversationDetail>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [
        "Conversations",
        { type: "Conversation", id },
      ],
    }),

    addChatMembers: builder.mutation<
      IChatConversationDetail,
      { id: number; userIds: number[] }
    >({
      query: ({ id, userIds }) => ({
        url: API_CHAT_CONVERSATION_ACTION(id, "members"),
        method: "POST",
        body: { userIds },
      }),
      transformResponse: (r: Envelope<IChatConversationDetail>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: "Conversation", id }],
    }),

    removeChatMember: builder.mutation<
      IChatConversationDetail,
      { id: number; userId: number }
    >({
      query: ({ id, userId }) => ({
        url: API_CHAT_MEMBER(id, userId),
        method: "DELETE",
      }),
      transformResponse: (r: Envelope<IChatConversationDetail>) => r.data,
      invalidatesTags: (_r, _e, { id }) => [{ type: "Conversation", id }],
    }),

    leaveChatConversation: builder.mutation<void, number>({
      query: (id) => ({
        url: API_CHAT_CONVERSATION_ACTION(id, "leave"),
        method: "POST",
      }),
      invalidatesTags: ["Conversations", "Unread"],
    }),

    /** "Delete chat" — for me only; it returns once someone writes again. */
    deleteChatConversation: builder.mutation<void, number>({
      query: (id) => ({ url: API_CHAT_CONVERSATION(id), method: "DELETE" }),
      invalidatesTags: (_r, _e, id) => [
        "Conversations",
        "Unread",
        "Media",
        { type: "Conversation", id },
      ],
    }),

    archiveChatConversation: builder.mutation<
      unknown,
      { id: number; archived: boolean }
    >({
      query: ({ id, archived }) => ({
        url: API_CHAT_CONVERSATION_ACTION(id, "archive"),
        method: "PATCH",
        body: { archived },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "Conversations",
        "Unread",
        { type: "Conversation", id },
      ],
    }),

    /**
     * Drag-and-drop in the pinned list: every pinned id in its new order, for
     * everyone. Applied to the open chat at once and rolled back on failure
     * (409 = the pins changed meanwhile: the detail is refetched).
     */
    reorderChatPins: builder.mutation<
      IChatMessage[],
      { conversationId: number; messageIds: number[] }
    >({
      query: ({ conversationId, messageIds }) => ({
        url: API_CHAT_CONVERSATION_PINS(conversationId),
        method: "PUT",
        body: { messageIds },
      }),
      transformResponse: (r: Envelope<IChatMessage[]>) => r.data,
      async onQueryStarted({ conversationId, messageIds }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          chatApi.util.updateQueryData("getChatConversation", conversationId, (draft) => {
            draft.pinnedMessages = reorderPinned(draft.pinnedMessages, messageIds);
          }),
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            chatApi.util.updateQueryData("getChatConversation", conversationId, (draft) => {
              draft.pinnedMessages = data;
            }),
          );
        } catch {
          patch.undo();
          dispatch(chatApi.util.invalidateTags([{ type: "Conversation", id: conversationId }]));
        }
      },
    }),

    /** Copies a message into up to 5 chats (all or none); the copies come back. */
    forwardChatMessage: builder.mutation<IChatMessage[], { id: number; conversationIds: number[] }>({
      query: ({ id, conversationIds }) => ({
        url: API_CHAT_MESSAGE_FORWARD(id),
        method: "POST",
        body: { conversationIds },
      }),
      transformResponse: (r: Envelope<IChatMessage[]>) => r.data,
      invalidatesTags: ["Conversations", "Media"],
    }),

    /** Drag-and-drop among my pinned chats: all of them, in their new order (top first). */
    reorderPinnedChats: builder.mutation<number[], number[]>({
      query: (conversationIds) => ({
        url: API_CHAT_PINNED_CONVERSATIONS,
        method: "PUT",
        body: { conversationIds },
      }),
      transformResponse: (r: Envelope<number[]>) => r.data,
      invalidatesTags: ["Conversations"],
    }),

    /** Only mine: pinned chats lead my list (at most 3; a fourth is refused). */
    pinChatConversation: builder.mutation<unknown, { id: number; pinned: boolean }>({
      query: ({ id, pinned }) => ({
        url: API_CHAT_CONVERSATION_ACTION(id, "pin"),
        method: "PATCH",
        body: { pinned },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "Conversations",
        "Unread",
        { type: "Conversation", id },
      ],
    }),

    muteChatConversation: builder.mutation<
      unknown,
      { id: number; duration: ChatMuteDuration }
    >({
      query: ({ id, duration }) => ({
        url: API_CHAT_CONVERSATION_ACTION(id, "mute"),
        method: "PATCH",
        body: { duration },
      }),
      invalidatesTags: (_r, _e, { id }) => [
        "Conversations",
        { type: "Conversation", id },
      ],
    }),

    markChatRead: builder.mutation<unknown, { id: number; messageId?: number }>({
      query: ({ id, messageId }) => ({
        url: API_CHAT_CONVERSATION_ACTION(id, "read"),
        method: "POST",
        body: messageId ? { messageId } : {},
      }),
      invalidatesTags: ["Unread", "Conversations"],
    }),

    sendChatMessage: builder.mutation<
      IChatMessage,
      { conversationId: number; clientMessageId: string; message: IChatOutgoingMessage }
    >({
      query: ({ conversationId, clientMessageId, message }) => ({
        url: API_CHAT_CONVERSATION_ACTION(conversationId, "messages"),
        method: "POST",
        body: { ...message, clientMessageId },
      }),
      transformResponse: (r: Envelope<IChatMessage>) => r.data,
      invalidatesTags: ["Conversations"],
    }),

    uploadChatAttachments: builder.mutation<IChatUploadedFile[], File[]>({
      query: (files) => {
        const body = new FormData();
        files.forEach((f) => body.append("files", f, f.name));
        return { url: API_CHAT_ATTACHMENTS, method: "POST", body };
      },
      transformResponse: (r: Envelope<IChatUploadedFile[]>) => r.data,
    }),

    editChatMessage: builder.mutation<IChatMessage, { id: number; body: string }>({
      query: ({ id, body }) => ({
        url: API_CHAT_MESSAGE(id),
        method: "PATCH",
        body: { body },
      }),
      transformResponse: (r: Envelope<IChatMessage>) => r.data,
    }),

    deleteChatMessage: builder.mutation<IChatMessage, number>({
      query: (id) => ({ url: API_CHAT_MESSAGE(id), method: "DELETE" }),
      transformResponse: (r: Envelope<IChatMessage>) => r.data,
      invalidatesTags: ["Conversations", "Media"],
    }),

    voteChatPoll: builder.mutation<IChatMessage, { id: number; optionIds: number[] }>({
      query: ({ id, optionIds }) => ({
        url: API_CHAT_MESSAGE_VOTE(id),
        method: "POST",
        body: { optionIds },
      }),
      transformResponse: (r: Envelope<IChatMessage>) => r.data,
    }),

    /**
     * I played someone else's voice note. Idempotent; the server ignores the
     * sender's own and answers `message_played` to whoever sees my receipts.
     */
    markChatMessagePlayed: builder.mutation<void, number>({
      query: (id) => ({ url: API_CHAT_MESSAGE_PLAYED(id), method: "POST" }),
    }),

    /** One emoji per person: replaces mine. Echoed as `message_updated`. */
    reactChatMessage: builder.mutation<IChatMessage, { id: number; emoji: string }>({
      query: ({ id, emoji }) => ({
        url: API_CHAT_MESSAGE_REACTION(id),
        method: "PUT",
        body: { emoji },
      }),
      transformResponse: (r: Envelope<IChatMessage>) => r.data,
    }),

    unreactChatMessage: builder.mutation<IChatMessage, number>({
      query: (id) => ({ url: API_CHAT_MESSAGE_REACTION(id), method: "DELETE" }),
      transformResponse: (r: Envelope<IChatMessage>) => r.data,
    }),

    /**
     * Pin / unpin for everyone (at most 3: a fourth unpins the oldest). Answers
     * the chat's pinned messages, written straight into the open chat's detail.
     */
    setChatMessagePinned: builder.mutation<
      IChatMessage[],
      { id: number; conversationId: number; pinned: boolean }
    >({
      query: ({ id, pinned }) => ({
        url: API_CHAT_MESSAGE_PIN(id),
        method: pinned ? "POST" : "DELETE",
      }),
      transformResponse: (r: Envelope<IChatMessage[]>) => r.data,
      async onQueryStarted({ conversationId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            chatApi.util.updateQueryData("getChatConversation", conversationId, (draft) => {
              draft.pinnedMessages = data;
            }),
          );
        } catch {
          // The caller shows the error.
        }
      },
    }),

    // ── Scheduled messages (only mine; keyed by conversation id) ──────────

    getChatScheduled: builder.query<IChatScheduledMessage[], number>({
      query: (conversationId) => ({
        url: API_CHAT_CONVERSATION_ACTION(conversationId, "scheduled"),
      }),
      transformResponse: (r: Envelope<IChatScheduledMessage[]>) => r.data,
      providesTags: (_r, _e, id) => [{ type: "Scheduled", id }],
    }),

    /** Same body as a send, plus `sendAt`; media are uploaded first. */
    scheduleChatMessage: builder.mutation<
      IChatScheduledMessage,
      { conversationId: number; message: IChatScheduleRequest }
    >({
      query: ({ conversationId, message }) => ({
        url: API_CHAT_CONVERSATION_ACTION(conversationId, "scheduled"),
        method: "POST",
        body: message,
      }),
      transformResponse: (r: Envelope<IChatScheduledMessage>) => r.data,
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: "Scheduled", id: conversationId },
      ],
    }),

    /** A failed one goes back to pending. */
    updateChatScheduled: builder.mutation<
      IChatScheduledMessage,
      { id: number; conversationId: number; sendAt?: string; body?: string }
    >({
      query: ({ id, sendAt, body }) => ({
        url: API_CHAT_SCHEDULED(id),
        method: "PATCH",
        body: { sendAt, body },
      }),
      transformResponse: (r: Envelope<IChatScheduledMessage>) => r.data,
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: "Scheduled", id: conversationId },
      ],
    }),

    /** Sends it now; the message also arrives over the socket as message_new. */
    sendChatScheduledNow: builder.mutation<
      IChatMessage,
      { id: number; conversationId: number }
    >({
      query: ({ id }) => ({ url: API_CHAT_SCHEDULED_SEND(id), method: "POST" }),
      transformResponse: (r: Envelope<IChatMessage>) => r.data,
      invalidatesTags: (_r, _e, { conversationId }) => [
        "Conversations",
        { type: "Scheduled", id: conversationId },
      ],
    }),

    cancelChatScheduled: builder.mutation<void, { id: number; conversationId: number }>({
      query: ({ id }) => ({ url: API_CHAT_SCHEDULED(id), method: "DELETE" }),
      invalidatesTags: (_r, _e, { conversationId }) => [
        { type: "Scheduled", id: conversationId },
      ],
    }),
  }),
});

export const {
  useGetChatCapabilitiesQuery,
  useGetChatUnreadSummaryQuery,
  useGetChatSettingsQuery,
  useSetChannelNoticeMutation,
  useUpdateChatSettingsMutation,
  useSetChatWallpaperMutation,
  useGetChatConversationsInfiniteQuery,
  useGetChatContactsInfiniteQuery,
  useGetChatConversationQuery,
  useLazyGetChatMessagesQuery,
  useSearchChatMessagesInfiniteQuery,
  useSearchAllChatMessagesInfiniteQuery,
  useGetChatMediaInfiniteQuery,
  useGetChatWorkStatusRecipientsQuery,
  useGetChatAudiencesQuery,
  useLazyGetChatAudienceMembersQuery,
  useGetChatShortcutItemsQuery,
  useOpenChatDirectMutation,
  useCreateChatGroupMutation,
  useCreateChatWorkStatusBroadcastMutation,
  useUpdateChatConversationMutation,
  useSetChatImageMutation,
  useAddChatMembersMutation,
  useRemoveChatMemberMutation,
  useLeaveChatConversationMutation,
  useArchiveChatConversationMutation,
  usePinChatConversationMutation,
  useReorderPinnedChatsMutation,
  useForwardChatMessageMutation,
  useDeleteChatConversationMutation,
  useMuteChatConversationMutation,
  useMarkChatReadMutation,
  useSendChatMessageMutation,
  useUploadChatAttachmentsMutation,
  useEditChatMessageMutation,
  useDeleteChatMessageMutation,
  useVoteChatPollMutation,
  useMarkChatMessagePlayedMutation,
  useReactChatMessageMutation,
  useSetChatMessagePinnedMutation,
  useReorderChatPinsMutation,
  useUnreactChatMessageMutation,
  useGetChatScheduledQuery,
  useScheduleChatMessageMutation,
  useUpdateChatScheduledMutation,
  useSendChatScheduledNowMutation,
  useCancelChatScheduledMutation,
} = chatApi;
